import type {
  ConsolidatedByYearsData,
  ModuleEntity,
} from '@/domains/careerLayer/careerLayer.entity';
import type { Day, DayName, RiskIndicator } from '@/domains/shared';

/**
 * Resultado final procesado listo para UI
 */
export interface ProcessedCareerLayerData {
  modulos: ModuleEntity[];
  indicadores_de_riesgo: RiskIndicator[];
  jornada: DayName | 'Ambas';
}

/**
 * UseCase para filtrar datos consolidados por jornada
 *
 * Responsabilidades:
 * - Recibir data consolidada por jornada
 * - Si recibe jornadaFilter (D o V): retorna solo esa jornada
 * - Si NO recibe jornadaFilter: consolida ambas jornadas (D + V)
 * - Retornar estructura plana lista para UI
 *
 * Ejemplo con filtro:
 * Input: { D: {...}, V: {...} }, jornadaFilter='D'
 * Output: { modulos: [...solo D], indicadores: [...solo D], jornada: 'Diurna' }
 *
 * Ejemplo sin filtro:
 * Input: { D: {...}, V: {...} }, jornadaFilter=null
 * Output: { modulos: [...D+V], indicadores: [...D+V consolidados], jornada: 'Ambas' }
 */
export class FilterCareerLayerByJornadaUseCase {
  /**
   * Ejecuta el filtrado por jornada
   * @param consolidatedData - Data ya consolidada por años
   * @param jornadaFilter - Jornada a filtrar ('Diurna' | 'Vespertina' | null)
   * @returns Data procesada lista para UI
   */
  execute(
    consolidatedData: ConsolidatedByYearsData,
    jornadaFilter: DayName | null,
  ): ProcessedCareerLayerData {
    // Si hay filtro de jornada, retornar solo esa jornada
    if (jornadaFilter) {
      const jornadaKey: Day = jornadaFilter === 'diurno' ? 'D' : 'V';
      const jornadaData = consolidatedData[jornadaKey];

      return {
        modulos: jornadaData.modulos,
        indicadores_de_riesgo: jornadaData.indicadores_de_riesgo,
        jornada: jornadaFilter,
      };
    }

    // Sin filtro: consolidar ambas jornadas
    const allModulos = [
      ...consolidatedData.D.modulos,
      ...consolidatedData.V.modulos,
    ];

    const consolidatedIndicators = this.mergeRiskIndicators(
      consolidatedData.D.indicadores_de_riesgo,
      consolidatedData.V.indicadores_de_riesgo,
    );

    return {
      modulos: allModulos,
      indicadores_de_riesgo: consolidatedIndicators,
      jornada: 'Ambas',
    };
  }

  /**
   * Consolida indicadores de ambas jornadas sumando valores
   * @param indicatorsD - Indicadores jornada Diurna
   * @param indicatorsV - Indicadores jornada Vespertina
   * @returns Indicadores consolidados
   */
  private mergeRiskIndicators(
    indicatorsD: RiskIndicator[],
    indicatorsV: RiskIndicator[],
  ): RiskIndicator[] {
    const merged = new Map<string, RiskIndicator>();

    // Procesar todos los indicadores
    [...indicatorsD, ...indicatorsV].forEach((indicator) => {
      const existing = merged.get(indicator.nombre);

      if (existing) {
        existing.bajo += indicator.bajo;
        existing.medio += indicator.medio;
        existing.alto += indicator.alto;
      } else {
        merged.set(indicator.nombre, { ...indicator });
      }
    });

    return Array.from(merged.values());
  }
}
